import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CompanyTypeEnum, IVisitorsArea } from '@smarttypes/core';
import { filter, first } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { GuestService } from '../../../services/guest.service';
import { MyGuestChatLinkService } from '../../../services/my-guest-chat-link.service';
import {
  isMyGuestCloseMessage,
  isMyGuestEmbedOrigin,
  openMyGuestTelHref,
  readMyGuestCallHref,
} from '../../../services/my-guest-embed-message.util';
import { buildMyGuestEmbedUrl, readVisitorsAreaLanguage } from '../../../services/my-guest-embed-url.util';
import { MyGuestUnreadCountService } from '../../../services/my-guest-unread-count.service';
import { VisitorsAreaService } from '../../../services/visitors-area.service';
import { VisitorsAreaV2Service } from '../../../services/visitors-area-v2.service';
import { UnreadCountBadgeComponent } from '../unread-count-badge/unread-count-badge.component';

const CLOSE_ANIMATION_MS = 320;
const MOBILE_FULLSCREEN_MQ = '(max-width: 767.98px)';

/** Hotel widgets home: `/`, `/w/:widgetId`, `/:guestObjectId`. */
const WIDGETS_HOME_PATH = /^(?:\/(?:w\/[^/]+|[a-fA-F0-9]{24})?)?$/;

@Component({
  selector: 'app-concierge-widget',
  standalone: true,
  imports: [AsyncPipe, UnreadCountBadgeComponent],
  templateUrl: './concierge-widget.component.html',
  styleUrls: ['./concierge-widget.component.scss'],
})
export class ConciergeWidgetComponent implements OnInit, OnDestroy {
  private readonly visitorsAreaService = inject(VisitorsAreaService);
  private readonly visitorsAreaV2Service = inject(VisitorsAreaV2Service);
  private readonly guestService = inject(GuestService);
  private readonly unreadCountService = inject(MyGuestUnreadCountService);
  private readonly chatLinkService = inject(MyGuestChatLinkService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  readonly unreadCount$ = this.unreadCountService.count$;

  isVisible = false;
  isOpen = false;
  iframeUrl?: SafeResourceUrl;

  private agentId = '';
  private guestId = '';
  private isOnWidgetsHome = false;
  private closeAnimationTimer?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.isOnWidgetsHome = this.#isWidgetsHomeUrl(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        this.isOnWidgetsHome = this.#isWidgetsHomeUrl(event.urlAfterRedirects);
        if (!this.isOnWidgetsHome && this.isOpen) {
          this.setOpen(false);
        }
        if (this.agentId) {
          this.#setGuestId(this.#readGuestIdFromStorage());
        }
        this.#syncVisibility();
      });
    this.visitorsAreaService
      .getConfig()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(config => this.#onConfig(config));
  }

  ngOnDestroy(): void {
    this.#clearCloseAnimationTimer();
    this.#setBodyScrollLocked(false);
    this.unreadCountService.setChatOpen(false);
    if (this.isVisible) {
      this.unreadCountService.stop();
    }
  }

  toggle(): void {
    this.setOpen(!this.isOpen);
  }

  close(): void {
    this.setOpen(false);
  }

  setOpen(open: boolean): void {
    if (open) {
      this.#clearCloseAnimationTimer();
      this.isOpen = true;
      this.unreadCountService.setChatOpen(true);
      this.#buildIframeUrl();
      this.#setBodyScrollLocked(true);
      return;
    }
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.unreadCountService.setChatOpen(false);
    this.#setBodyScrollLocked(false);
    this.#clearCloseAnimationTimer();
    // Keep iframe mounted until scale/fade-out finishes.
    this.closeAnimationTimer = setTimeout(() => {
      this.iframeUrl = undefined;
      this.closeAnimationTimer = undefined;
    }, CLOSE_ANIMATION_MS);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    if (!isMyGuestEmbedOrigin(event.origin, environment.myGuestUrl)) {
      return;
    }
    if (isMyGuestCloseMessage(event.data)) {
      this.close();
      return;
    }
    const href = readMyGuestCallHref(event.data);
    if (href) {
      openMyGuestTelHref(href);
    }
  }

  #onConfig(config: IVisitorsArea): void {
    this.agentId = (config.myGuestAgentId ?? '').trim();
    if (!this.agentId) {
      this.#syncVisibility();
      return;
    }
    this.#setGuestId(this.#resolveGuestId(config));
    this.#syncVisibility();
  }

  /**
   * Zmiana gościa w tej samej karcie unieważnia zapamiętany token — należał do poprzedniego gościa i
   * prowadziłby do jego rozmowy.
   */
  #setGuestId(next: string): void {
    if (next === this.guestId) {
      return;
    }
    this.guestId = next;
    this.iframeUrl = undefined;
    this.chatLinkService.reset();
  }

  #resolveGuestId(config: IVisitorsArea): string {
    if (config.mainComponent === CompanyTypeEnum.apartments) {
      return (this.visitorsAreaV2Service.guestId ?? this.guestService.getGuestId() ?? '').trim();
    }
    return this.#readGuestIdFromStorage();
  }

  #readGuestIdFromStorage(): string {
    return (this.guestService.getGuestId() || this.visitorsAreaV2Service.guestId || '').trim();
  }

  #isWidgetsHomeUrl(url: string): boolean {
    const path = url.split('?')[0].split('#')[0] || '/';
    return WIDGETS_HOME_PATH.test(path);
  }

  #syncVisibility(): void {
    const shouldShow = !!this.agentId && !!this.guestId && this.isOnWidgetsHome;
    if (this.isVisible && !shouldShow) {
      this.setOpen(false);
      this.unreadCountService.stop();
    }
    if (!this.isVisible && shouldShow) {
      this.unreadCountService.start();
    }
    this.isVisible = shouldShow;
  }

  /**
   * Adres iframe powstaje z tokenu linku, nie z `agentId`/`guestId`: myGuest nie przyjmuje już
   * tożsamości gościa z przeglądarki (każdy mógłby wskazać dowolnego gościa), więc token mintuje
   * nasz backend, a tutaj tylko go doklejamy.
   *
   * Asynchroniczne — panel jest już otwarty, a iframe montuje się (`@if (iframeUrl)`), gdy token
   * dojdzie. Brak tokenu zostawia panel pusty; badge i FAB działają dalej.
   */
  #buildIframeUrl(): void {
    const baseUrl = environment.myGuestUrl?.replace(/\/$/, '');
    if (!baseUrl || !this.agentId || !this.guestId) {
      this.iframeUrl = undefined;
      return;
    }
    this.chatLinkService
      .getToken()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(token => {
        if (!token || !this.isOpen) {
          return;
        }
        const url = buildMyGuestEmbedUrl(
          baseUrl,
          token,
          readVisitorsAreaLanguage(this.translateService.currentLang),
        );
        this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      });
  }

  #clearCloseAnimationTimer(): void {
    if (this.closeAnimationTimer === undefined) {
      return;
    }
    clearTimeout(this.closeAnimationTimer);
    this.closeAnimationTimer = undefined;
  }

  #setBodyScrollLocked(locked: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }
    const isMobile = window.matchMedia(MOBILE_FULLSCREEN_MQ).matches;
    if (!isMobile && locked) {
      return;
    }
    document.body.style.overflow = locked ? 'hidden' : '';
  }
}
